/** Thousands grouping: "1.234", "1,234,567" (one separator kind, groups of 3 after the first). */
const GROUPED = /^\d{1,3}(?:([.,])\d{3})(?:\1\d{3})*$/;

/** Shared normalisation: sign + integer and fraction digit strings (grouping removed), or null when invalid. */
function normalize(text: string | null | undefined, dec: string): { neg: boolean; int: string; frac: string } | null {
  if (text == null) return null;
  const t = text.replace(/[\s  €]/g, '');
  const m = /^([-−+]?)(\d[\d.,]*)$|^([-−+]?)([.,]\d+)$/.exec(t);
  if (!m) return null;
  const neg = m[1] === '-' || m[1] === '−' || m[3] === '-' || m[3] === '−';
  const body = (m[2] ?? m[4])!;
  if (/[.,]$/.test(body)) return null;
  const last = Math.max(body.lastIndexOf(','), body.lastIndexOf('.'));
  if (last < 0) return { neg, int: body, frac: '' };
  const sep = body[last]!;
  const tail = body.slice(last + 1);
  if (sep !== dec && tail.length === 3 && GROUPED.test(body)) return { neg, int: body.replace(/[.,]/g, ''), frac: '' };
  const intPart = body.slice(0, last);
  if (/[.,]/.test(intPart)) {
    const g = GROUPED.exec(intPart);
    if (!g || g[1] === sep) return null;
  }
  return { neg, int: intPart.replace(/[.,]/g, '') || '0', frac: tail };
}

/**
 * Locale-safe number parsing for typed input and CSV cells (pure, no locale state). The single parser for web, mobile
 * and server: never returns NaN.
 * `dec` is the expected decimal separator; it is always decimal. The other separator is decimal only when it is
 * not followed by exactly 3 digits (dec ',': "1.234" = 1234, "12.5" = 12,5, "438,9";
 * dec '.': "2,384.10", "1,234" = 1234, "12,50" = 12.5). Thousands groups must be well formed ("1.2.3" is invalid).
 * Spaces (incl. NBSP/NNBSP), "€" and a leading "+" are ignored; "-" and "−" mark negatives.
 * Returns null for empty or invalid input.
 */
export function parseDecimal(text: string | null | undefined, dec: ',' | '.' | string = ','): number | null {
  const p = normalize(text, dec);
  if (!p) return null;
  const n = Number(p.frac ? `${p.int}.${p.frac}` : p.int);
  if (!Number.isFinite(n)) return null;
  return p.neg && n !== 0 ? -n : n;
}

/**
 * Money text → integer cents, built from the digit strings (no float math, so "1,005" can't drift). At most 2
 * decimals: "19,999" is invalid, not rounded. Null for empty or invalid input.
 */
export function parseMoneyCents(text: string | null | undefined, dec: ',' | '.' | string = ','): number | null {
  const p = normalize(text, dec);
  if (!p || p.frac.length > 2) return null;
  const cents = Number(p.int) * 100 + Number(p.frac.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents)) return null;
  return p.neg && cents !== 0 ? -cents : cents;
}
